import { useState, useEffect } from 'react';
import axios from 'axios';
import { Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaterialityAssessment = () => {
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'q1',
      question: 'How significant is energy consumption to your business operations?',
      options: ['Very High', 'High', 'Moderate', 'Low']
    },
    {
      id: 'q2',
      question: 'What is the level of water usage in your operations?',
      options: ['Very High', 'High', 'Moderate', 'Low']
    },
    {
      id: 'q3',
      question: 'How much waste does your business generate?',
      options: ['Very High', 'High', 'Moderate', 'Low']
    },
    {
      id: 'q4',
      question: 'How important are labor practices and employee wellbeing to your stakeholders?',
      options: ['Very Important', 'Important', 'Somewhat Important', 'Not Important']
    },
    {
      id: 'q5',
      question: 'What is the environmental impact of your supply chain?',
      options: ['Very High', 'High', 'Moderate', 'Low']
    },
    {
      id: 'q6',
      question: 'How significant is community engagement to your business?',
      options: ['Very Significant', 'Significant', 'Somewhat Significant', 'Not Significant']
    }
  ];

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/materiality`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching materiality results:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const answerArray = Object.entries(answers).map(([id, answer]) => {
      const question = questions.find(q => q.id === id);
      return {
        question: question.question,
        answer: answer
      };
    });

    try {
      await axios.post(`${API}/materiality`, answerArray);
      await fetchResults();
      setShowResults(true);
      toast.success('Materiality assessment completed', {
        description: 'Your results are ready'
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Failed to submit assessment');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-[#C85A32] text-white';
      case 'medium':
        return 'bg-[#D5CABD] text-[#111814]';
      case 'low':
        return 'bg-[#8B9D8F] text-white';
      default:
        return 'bg-gray-300 text-gray-800';
    }
  };

  return (
    <div className="p-8" data-testid="materiality-assessment-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Materiality Assessment
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Identify the ESG topics most relevant to your business
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Assessment Form */}
        <div className="lg:col-span-2">
          <Card className="border-[#E5E1D8]">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Assessment Questionnaire</CardTitle>
              <CardDescription>Answer these questions to determine your material ESG topics</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="materiality-form">
                {questions.map((q, index) => (
                  <div key={q.id} className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                    <Label className="text-base font-semibold text-[#111814] mb-3 block">
                      {index + 1}. {q.question}
                    </Label>
                    <RadioGroup
                      value={answers[q.id] || ''}
                      onValueChange={(value) => setAnswers({ ...answers, [q.id]: value })}
                      data-testid={`question-${q.id}`}
                    >
                      <div className="space-y-2">
                        {q.options.map((option) => (
                          <div key={option} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${q.id}-${option}`} data-testid={`${q.id}-${option.toLowerCase().replace(' ', '-')}`} />
                            <Label htmlFor={`${q.id}-${option}`} className="cursor-pointer text-[#4B5563]">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>
                ))}
                <Button
                  type="submit"
                  className="w-full bg-[#1A362D] hover:bg-[#2D4A3E]"
                  disabled={Object.keys(answers).length < questions.length}
                  data-testid="submit-assessment-button"
                >
                  Submit Assessment
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Results Panel */}
        <div>
          <Card className="border-[#E5E1D8]" data-testid="materiality-results">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-[#1A362D]" />
                <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Material Topics</CardTitle>
              </div>
              <CardDescription>Prioritized ESG topics for your business</CardDescription>
            </CardHeader>
            <CardContent>
              {results.length > 0 ? (
                <div className="space-y-3">
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className="p-3 bg-white rounded-lg border border-[#E5E1D8] hover:shadow-sm transition-all duration-200"
                      data-testid={`result-${index}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold text-[#111814] text-sm">{result.topic}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(result.priority)}`}>
                          {result.priority}
                        </span>
                      </div>
                      <div className="w-full bg-[#F2F0EB] rounded-full h-2">
                        <div
                          className="bg-[#1A362D] h-2 rounded-full transition-all duration-500"
                          style={{ width: `${(result.score / 10) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#F2F0EB] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-8 h-8 text-[#6B7280]" />
                  </div>
                  <p className="text-sm text-[#6B7280]">Complete the assessment to see your material topics</p>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default MaterialityAssessment;
